/**
 * JS facade over the Rust sync store (src-tauri/src/sync/) — the only place
 * the webview talks to workspace_db_* commands or sync engine events. The UI
 * never touches SQLite (or sync targets) except through here.
 *
 * Dormant in Phase 0: no call sites route through this yet. The snapshot
 * shape matches workspaceStoreRouter.js exactly ({ workspaceName, collections,
 * globalVariables, workspaceVariables, environments, selectedEnvironmentId,
 * schemas }) so Phase 1 is a router swap.
 */
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { makeSafeUnlisten } from '../utils/safeTauriEvent';
import { isTauri } from '../utils/tauri';

/** Tauri events emitted by the Rust engine. */
export const SYNC_EVENTS = Object.freeze({
  STATUS: 'sync:status',
  REMOTE_APPLIED: 'sync:remote-applied',
  GIT_FLUSH_NEEDED: 'sync:git-flush-needed',
  // COMMS-12: the server warns on a successful push ack when this device's
  // clock looks broken (sync.ex's clock_skew_ms/2) — carries
  // { workspaceId, clockSkewMs }. See src-tauri/src/sync/engine.rs's
  // EVENT_CLOCK_SKEW.
  CLOCK_SKEW: 'sync:clock-skew',
  // The server REFUSED pushed ops (plan quota, permission, …) — carries
  // { workspaceId, rejected: [{ entityId, entityType, error }] } with the
  // server's own message verbatim. See src-tauri/src/sync/rejection.rs and
  // services/syncRejections.js. Distinct from STATUS on purpose: a rejection
  // leaves the transport perfectly healthy, so the engine reports `idle`
  // immediately afterwards having quietly abandoned the user's row.
  REJECTED: 'sync:rejected',
});

function assertTauri() {
  if (!isTauri()) {
    throw new Error('syncStore requires the desktop app (no browser fallback)');
  }
}

/** @returns {Promise<object>} full snapshot for the workspace */
export function loadDbSnapshot(workspaceId) {
  assertTauri();
  return invoke('workspace_db_load_snapshot', { workspaceId });
}

/**
 * Diff-writes the snapshot into rows and appends outbox ops in one
 * transaction (Rust side). `options` mirrors saveWorkspaceSnapshot's options
 * (e.g. skipEmptyArrays semantics live in Rust).
 */
export function saveDbSnapshot(workspaceId, snapshot, options = {}) {
  assertTauri();
  return invoke('workspace_db_save_snapshot', { workspaceId, snapshot, options });
}

/** Migration import: writes rows without emitting outbox ops. */
export function importDbSnapshot(workspaceId, snapshot) {
  assertTauri();
  return invoke('workspace_db_import_snapshot', { workspaceId, snapshot });
}

export function getDevicePref(workspaceId, key) {
  assertTauri();
  return invoke('workspace_db_get_pref', { workspaceId, key });
}

export function setDevicePref(workspaceId, key, value) {
  assertTauri();
  return invoke('workspace_db_set_pref', { workspaceId, key, value });
}

/** Removes every row for the workspace (one transaction, not a file delete). */
export function wipeWorkspaceDb(workspaceId) {
  assertTauri();
  return invoke('workspace_db_wipe', { workspaceId });
}

/**
 * Phase 4 inbound (git) path: apply a snapshot parsed from the on-disk YAML
 * tree. `fullRebuild = true` wipes + imports with no outbox (fresh clone /
 * older-commit restore); `fullRebuild = false` diff-applies with outbox ops,
 * tombstoning entities missing from the snapshot (deletions round-trip).
 */
export function applyExternalDbSnapshot(workspaceId, snapshot, fullRebuild = false) {
  assertTauri();
  return invoke('workspace_db_apply_external_snapshot', {
    workspaceId,
    snapshot,
    fullRebuild,
  });
}

/**
 * Phase 4: record how far the git target has consumed the outbox
 * (sync_state (workspace_id,'git').last_acked_outbox_seq — monotonic upsert).
 */
export function ackGitFlush(workspaceId, upToOutboxSeq) {
  assertTauri();
  return invoke('workspace_db_git_ack', { workspaceId, upToOutboxSeq });
}

/**
 * Highest outbox seq for a workspace (0 when empty). Read BEFORE loading the
 * snapshot to flush, so the post-flush ack never claims later ops.
 */
export function getOutboxMaxSeq(workspaceId) {
  assertTauri();
  return invoke('workspace_db_outbox_max_seq', { workspaceId });
}

/**
 * Subscribe to a sync engine event. Returns an async unsubscribe fn (safe to
 * call twice / after teardown).
 * @param {string} event one of SYNC_EVENTS
 * @param {(payload: object) => void} handler
 */
export function subscribeSyncEvent(event, handler) {
  assertTauri();
  const unlistenPromise = listen(event, (e) => handler(e.payload));
  return makeSafeUnlisten(async () => {
    const unlisten = await unlistenPromise;
    return unlisten();
  });
}
